import React, { useEffect, useId, useRef } from 'react';
import SvgIcon from '@mui/material/SvgIcon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import cn from 'classnames';

/** Paths from Command Intelligence brand SVG assets */
const SPARK_LARGE = 'M53 8 L55.2 16 L63 18.2 L55.2 20.4 L53 28.4 L50.8 20.4 L43 18.2 L50.8 16 Z';
const SPARK_SMALL = 'M45 24 L46 28 L50 29 L46 30 L45 34 L44 30 L40 29 L44 28 Z';

const SHIMMER_DURATION = '5s';
const SHIMMER_TRANSLATE = '-96 0; 96 0; -96 0';
const SHIMMER_KEY_TIMES = '0; 0.5; 1';
const SHIMMER_KEY_SPLINES = '0.45 0 0.55 1; 0.45 0 0.55 1';

const SHIMMER_CYCLE_MS = 9000;
const SHIMMER_BLUE_HOLD_END = 0.3;
const SHIMMER_FORWARD_SWEEP_END = 0.48;
const SHIMMER_PURPLE_HOLD_END = 0.56;
const SHIMMER_REVERSE_SWEEP_END = 0.74;

/** App-tile overlay sweep axis — long enough for the icon to sit in the transparent zone at rest. */
const APP_TILE_SWEEP_AXIS = {
  x1: -48,
  y1: 32,
  x2: 144,
  y2: 32,
};

const APP_TILE_SWEEP_BLUE_TX = 56;
const APP_TILE_SWEEP_PURPLE_TX = -80;
const APP_TILE_SWEEP_INITIAL = `translate(${APP_TILE_SWEEP_BLUE_TX} 0)`;

const smoothstep = progress => progress * progress * (3 - 2 * progress);

const computeAppTileSweepTranslateX = now => {
  const t = (now % SHIMMER_CYCLE_MS) / SHIMMER_CYCLE_MS;
  let tx = APP_TILE_SWEEP_BLUE_TX;

  if (t < SHIMMER_BLUE_HOLD_END) {
    tx = APP_TILE_SWEEP_BLUE_TX;
  } else if (t < SHIMMER_FORWARD_SWEEP_END) {
    const progress = smoothstep((t - SHIMMER_BLUE_HOLD_END) / (SHIMMER_FORWARD_SWEEP_END - SHIMMER_BLUE_HOLD_END));
    tx = APP_TILE_SWEEP_BLUE_TX + (APP_TILE_SWEEP_PURPLE_TX - APP_TILE_SWEEP_BLUE_TX) * progress;
  } else if (t < SHIMMER_PURPLE_HOLD_END) {
    tx = APP_TILE_SWEEP_PURPLE_TX;
  } else if (t < SHIMMER_REVERSE_SWEEP_END) {
    const progress = smoothstep((t - SHIMMER_PURPLE_HOLD_END) / (SHIMMER_REVERSE_SWEEP_END - SHIMMER_PURPLE_HOLD_END));
    tx = APP_TILE_SWEEP_PURPLE_TX + (APP_TILE_SWEEP_BLUE_TX - APP_TILE_SWEEP_PURPLE_TX) * progress;
  }

  return tx;
};

const useAppTileOverlayTransform = (enabled, gradientRef) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let frameId;
    let cancelled = false;

    const tick = now => {
      if (cancelled) {
        return;
      }

      const gradientEl = gradientRef?.current;

      if (gradientEl) {
        gradientEl.setAttribute('gradientTransform', `translate(${computeAppTileSweepTranslateX(now)} 0)`);
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [enabled, gradientRef]);
};

export const COMMAND_INTELLIGENCE_ICON_SIZES = {
  header: { width: 62.5, height: 45 },
  sidebar: { width: 24, height: 24 },
  md: { width: 32, height: 32 },
  appTray: { width: 40, height: 40 },
  appSwitcher: { width: 64, height: 64 },
};

/** Matches app tray CSS in app-icons/styles.js (40px desktop, 83px below 430px). */
export const COMMAND_INTELLIGENCE_APP_TRAY_SX = {
  width: '40px',
  height: '40px',
  '@media screen and (max-width: 430px)': {
    width: '83px',
    height: '83px',
  },
};

/** Same diagonal blend as connex-cds app-icons (mt.svg, etc.), scaled to 64×64 viewBox. */
const APP_ICON_BLEND_AXIS = {
  x1: -16.91,
  y1: -14.67,
  x2: 54.44,
  y2: 56.69,
};

const APP_ICON_BLEND = {
  light: '#5BA4EA',
  dark: '#14497B',
};

const SHIMMER_GRADIENT_AXIS = {
  x1: -32,
  y1: 32,
  x2: 96,
  y2: 32,
};

const iconGradient = {
  light: {
    start: '#14497B',
    bridge: '#6366F1',
    end: '#9333EA',
    peak: '#C4B5FD',
  },
  dark: {
    start: '#5BA4EA',
    bridge: '#818CF8',
    end: '#A855F7',
    peak: '#E9D5FF',
  },
};

const sidebarGradient = {
  light: iconGradient.light,
  dark: {
    start: '#93C5FD',
    bridge: '#A5B4FC',
    end: '#C084FC',
    peak: '#FAF5FF',
  },
};

const pickForMode = (validateMode, mode, lightValue, darkValue) => {
  if (mode === 'dark') {
    return darkValue;
  }
  if (mode === 'light') {
    return lightValue;
  }
  return validateMode?.({ light: lightValue, dark: darkValue }) ?? lightValue;
};

const resolveIconPalette = (palette, validateMode, mode) => {
  if (palette === 'sidebar') {
    return {
      gradient: {
        start: pickForMode(validateMode, mode, sidebarGradient.light.start, sidebarGradient.dark.start),
        bridge: pickForMode(validateMode, mode, sidebarGradient.light.bridge, sidebarGradient.dark.bridge),
        end: pickForMode(validateMode, mode, sidebarGradient.light.end, sidebarGradient.dark.end),
        peak: pickForMode(validateMode, mode, sidebarGradient.light.peak, sidebarGradient.dark.peak),
      },
      lettermarkFill: pickForMode(validateMode, mode, 'gradient', '#FFFFFF'),
    };
  }

  return {
    gradient: {
      start: pickForMode(validateMode, mode, iconGradient.light.start, iconGradient.dark.start),
      bridge: pickForMode(validateMode, mode, iconGradient.light.bridge, iconGradient.dark.bridge),
      end: pickForMode(validateMode, mode, iconGradient.light.end, iconGradient.dark.end),
      peak: pickForMode(validateMode, mode, iconGradient.light.peak, iconGradient.dark.peak),
    },
    lettermarkFill: pickForMode(validateMode, mode, 'gradient', '#FFFFFF'),
  };
};

const WHITE_TILE_ICON_SIZES = new Set(['appTray', 'appSwitcher']);

const IconShapes = ({ paint }) => {
  const ringProps = {
    cx: 29,
    cy: 33,
    r: 21,
    fill: 'none',
    stroke: paint,
    strokeWidth: 3.5,
    strokeLinecap: 'round',
    strokeDasharray: '112 20',
    transform: 'rotate(35 29 33)',
  };

  const textProps = {
    x: 29,
    y: 41,
    fontFamily: "'IBM Plex Sans', system-ui, -apple-system, sans-serif",
    fontSize: 21,
    fontWeight: 500,
    fill: paint,
    textAnchor: 'middle',
  };

  return (
    <>
      <circle {...ringProps} />
      <text {...textProps}>ai</text>
      <path d={SPARK_LARGE} fill={paint} />
      <path d={SPARK_SMALL} fill={paint} />
    </>
  );
};

const SHIMMER_PEAK_COLOR = '#FAF5FF';
const SHIMMER_BRIDGE_COLOR = '#A5B4FC';

const AppTileOverlayGradientStops = () => (
  <>
    <stop offset="0%" stopColor="#9333EA" stopOpacity="0" />
    <stop offset="38%" stopColor="#9333EA" stopOpacity="0" />
    <stop offset="38.125%" stopColor={SHIMMER_BRIDGE_COLOR} stopOpacity="0.85" />
    <stop offset="38.75%" stopColor={SHIMMER_PEAK_COLOR} stopOpacity="1" />
    <stop offset="39.5%" stopColor={SHIMMER_PEAK_COLOR} stopOpacity="1" />
    <stop offset="40.25%" stopColor={SHIMMER_PEAK_COLOR} stopOpacity="1" />
    <stop offset="40.875%" stopColor={SHIMMER_BRIDGE_COLOR} stopOpacity="0.85" />
    <stop offset="41.75%" stopColor="#6366F1" stopOpacity="1" />
    <stop offset="72%" stopColor="#9333EA" stopOpacity="1" />
    <stop offset="100%" stopColor="#9333EA" stopOpacity="1" />
  </>
);

const CommandIntelligenceIcon = ({ size, palette = 'theme', mode, sx, className, ...props }) => {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const idSuffix = useId()?.replace(/:/g, '');
  const overlayGradientRef = useRef(null);
  const themeGradientId = `ci-ai-icon-g-${idSuffix}`;
  const baseGradientId = `ci-ai-icon-base-${idSuffix}`;
  const overlayGradientId = `ci-ai-icon-overlay-${idSuffix}`;

  const presetSizeSx = size ? COMMAND_INTELLIGENCE_ICON_SIZES?.[size] : undefined;
  const resolvedSizeSx = size === 'appTray' ? COMMAND_INTELLIGENCE_APP_TRAY_SX : presetSizeSx;
  const useAppIconBlend = WHITE_TILE_ICON_SIZES.has(size) || palette === 'blue';
  const resolvedMode = WHITE_TILE_ICON_SIZES.has(size) ? 'light' : mode ?? theme?.palette?.mode;
  const { gradient, lettermarkFill: lettermarkMode } = resolveIconPalette(
    useAppIconBlend ? 'theme' : palette,
    theme?.validateMode,
    resolvedMode
  );
  const lettermarkFill = lettermarkMode === 'gradient' ? `url(#${themeGradientId})` : lettermarkMode;
  const showShimmer = !prefersReducedMotion;

  useAppTileOverlayTransform(useAppIconBlend && showShimmer, overlayGradientRef);

  return (
    <SvgIcon
      className={cn('command-intelligence-icon', className)}
      viewBox="0 0 64 64"
      aria-hidden="true"
      sx={{
        ...resolvedSizeSx,
        '--ci-icon-gradient-start': gradient.start,
        '--ci-icon-gradient-bridge': gradient.bridge,
        '--ci-icon-gradient-end': gradient.end,
        '--ci-icon-gradient-peak': gradient.peak,
        '--ci-icon-lettermark-fill': lettermarkFill,
        ...sx,
      }}
      {...props}
    >
      <defs>
        {useAppIconBlend ? (
          <>
            <linearGradient
              id={baseGradientId}
              x1={APP_ICON_BLEND_AXIS.x1}
              y1={APP_ICON_BLEND_AXIS.y1}
              x2={APP_ICON_BLEND_AXIS.x2}
              y2={APP_ICON_BLEND_AXIS.y2}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={APP_ICON_BLEND.light} />
              <stop offset="100%" stopColor={APP_ICON_BLEND.dark} />
            </linearGradient>
            <linearGradient
              ref={overlayGradientRef}
              id={overlayGradientId}
              x1={APP_TILE_SWEEP_AXIS.x1}
              y1={APP_TILE_SWEEP_AXIS.y1}
              x2={APP_TILE_SWEEP_AXIS.x2}
              y2={APP_TILE_SWEEP_AXIS.y2}
              gradientUnits="userSpaceOnUse"
              gradientTransform={showShimmer ? APP_TILE_SWEEP_INITIAL : undefined}
            >
              <AppTileOverlayGradientStops />
            </linearGradient>
          </>
        ) : (
          <linearGradient
            id={themeGradientId}
            x1={SHIMMER_GRADIENT_AXIS.x1}
            y1={SHIMMER_GRADIENT_AXIS.y1}
            x2={SHIMMER_GRADIENT_AXIS.x2}
            y2={SHIMMER_GRADIENT_AXIS.y2}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="var(--ci-icon-gradient-start)" />
            <stop offset="34%" stopColor="var(--ci-icon-gradient-start)" />
            <stop offset="44%" stopColor="var(--ci-icon-gradient-bridge)" />
            <stop offset="50%" stopColor="var(--ci-icon-gradient-peak)" />
            <stop offset="56%" stopColor="var(--ci-icon-gradient-bridge)" />
            <stop offset="66%" stopColor="var(--ci-icon-gradient-end)" />
            <stop offset="100%" stopColor="var(--ci-icon-gradient-end)" />
            {showShimmer ? (
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                values={SHIMMER_TRANSLATE}
                keyTimes={SHIMMER_KEY_TIMES}
                calcMode="spline"
                keySplines={SHIMMER_KEY_SPLINES}
                dur={SHIMMER_DURATION}
                repeatCount="indefinite"
              />
            ) : null}
          </linearGradient>
        )}
      </defs>

      {useAppIconBlend ? (
        <>
          <IconShapes paint={`url(#${baseGradientId})`} />
          {showShimmer ? <IconShapes paint={`url(#${overlayGradientId})`} /> : null}
        </>
      ) : (
        <>
          <circle
            cx="29"
            cy="33"
            r="21"
            fill="none"
            stroke={`url(#${themeGradientId})`}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="112 20"
            transform="rotate(35 29 33)"
          />
          <text
            x="29"
            y="41"
            fontFamily="'IBM Plex Sans', system-ui, -apple-system, sans-serif"
            fontSize="21"
            fontWeight="500"
            fill="var(--ci-icon-lettermark-fill)"
            textAnchor="middle"
          >
            ai
          </text>
          <path d={SPARK_LARGE} fill={`url(#${themeGradientId})`} />
          <path d={SPARK_SMALL} fill={`url(#${themeGradientId})`} />
        </>
      )}
    </SvgIcon>
  );
};

export default CommandIntelligenceIcon;
